import React, { useState, useEffect, useMemo, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { updateBarcode } from "../../../graphql/barcodes/custom-mutations";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { logError } from "../../../utils/appUtils";
import { QrBarcodeScanner } from "../../../components/common/QrBarcodeScanner/QrBarcodeScanner";
import { beep } from "../../../utils/utils";
import { BarcodesStatus } from "../../../utils/constants";
import { BarcodeInputIcon } from "../../../components/common/BarcodeInputIcon";

const useStyles = makeStyles((theme) => ({
  scrollableContainer: {
    maxHeight: "40vh",
    overflowY: "auto",
    padding: theme.spacing(1),
    "& > *:not(:last-child)": {
      marginBottom: theme.spacing(1),
    },
  },
  dialogActions: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
  },
  highlighted: {
    backgroundColor: "rgba(0, 150, 70, 0.44)",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)", // soft shadow
    transition: "all 0.3s ease-in-out",
    padding: "4px 8px",
  },
}));

export const ExpectedBarcodeScanDialog = ({
  open,
  onClose,
  onSave,
  expectedBarcodes = [],
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  const [barcodeList, setBarcodeList] = useState([]);
  const [invalidScanMessage, setInvalidScanMessage] = useState("");
  const [tempBarcode, setTempBarcode] = useState("");
  const inputRef = useRef(null);
  const notify = useNotify();
  const [hiddenBarcodes, setHiddenBarcodes] = useState([]);
  const [recentlyAddedBarcode, setRecentlyAddedBarcode] = useState(null);
  const totalExpected = useMemo(
    () => expectedBarcodes.length,
    [expectedBarcodes]
  );
  const loadedQuantity = useMemo(
    () =>
      expectedBarcodes.filter((b) => b.status === BarcodesStatus.LOADED)
        .length || 0,
    [expectedBarcodes]
  );

  const totalQuantity = loadedQuantity + barcodeList.length;
  const manuallyAddedBarcodes = expectedBarcodes.filter(
    (barcode) =>
      barcode.isScanned === false &&
      barcode.status !== BarcodesStatus.LOADED &&
      !hiddenBarcodes.includes(barcode.id)
  );

  useEffect(() => {
    if (invalidScanMessage) {
      const timer = setTimeout(() => setInvalidScanMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [invalidScanMessage]);

  const barcodesLoaded = async (barcode) => {
    const scannedBy = window?.appUser?.fullName || "Unknown User";
    try {
      const result = await API.graphql(
        graphqlOperation(updateBarcode, {
          input: {
            ...barcode,
            status: BarcodesStatus.LOADED,
            scannedOut: scannedBy,
          },
        })
      );
      const updatedBarcode = result.data.updateBarcode;
      setBarcodeList((prev) => {
        const updated = [...prev, updatedBarcode];
        return updated;
      });
    } catch (error) {
      logError("ExpectedBarcodeScan:barcodesLoaded", error, notify);
      setInvalidScanMessage("Failed to mark barcode as loaded.");
    }
  };

  const handleSuccess = async (scannedValue) => {
    const matchedBarcode = expectedBarcodes.filter(
      (item) => item.value === scannedValue
    );

    if (matchedBarcode.length > 0) {
      const alreadyScannedIds = new Set(barcodeList.map((b) => b.id));
      const nextUnscanned = matchedBarcode.find(
        (item) => !alreadyScannedIds.has(item.id)
      );

      if (nextUnscanned) {
        await barcodesLoaded(nextUnscanned);
        beep(100, 520, 200);
        return nextUnscanned;
      } else {
        setInvalidScanMessage("Barcode already scanned.");
      }
    } else {
      beep(180, 200, 300);
      setInvalidScanMessage(`Barcode "${scannedValue}" is not in the system.`);
    }
  };

  const handleAddBarcode = async () => {
    if (!tempBarcode.trim()) return;

    setTempBarcode("");
    const barcodeValue = tempBarcode.trim();

    const matched = await handleSuccess(barcodeValue);
    if (matched) {
      setRecentlyAddedBarcode(matched.id);

      setTimeout(() => {
        setHiddenBarcodes((prev) => [...prev, matched.id]);
        setRecentlyAddedBarcode(null);
      }, 1500);
    }
    inputRef.current?.focus();
  };

  const handleClose = () => {
    onSave(totalQuantity);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth fullScreen={smDown}>
      <Box
        component="span"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        m={2}
      >
        <Typography variant="h6">
          Scan to Add ({totalQuantity}/{totalExpected})
        </Typography>
        <CancelOutlinedIcon
          fontSize="large"
          onClick={() => onSave(totalQuantity)}
        />
      </Box>
      <DialogContent>
        <QrBarcodeScanner onScanSuccess={handleSuccess} />

        {invalidScanMessage && (
          <Box mt={2}>
            <Typography color="error" variant="body2">
              {invalidScanMessage}
            </Typography>
          </Box>
        )}
        <Grid item xs={12} container spacing={1} alignItems="center">
          <Grid item xs>
            <TextField
              inputRef={inputRef}
              label="Barcode"
              variant="outlined"
              fullWidth
              margin="dense"
              value={tempBarcode}
              onChange={(e) => {
                const input = e.target.value;
                const alphanumericOnly = input
                  .replace(/[^a-z0-9]/gi, "")
                  .toUpperCase();
                setTempBarcode(alphanumericOnly);
              }}
            />
          </Grid>
          <Grid item>
            {smUp ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddBarcode}
                startIcon={<AddCircleIcon />}
              >
                ADD
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddBarcode}
              >
                <AddCircleIcon />
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.scrollableContainer}>
          {manuallyAddedBarcodes.length > 0 ? (
            <Typography variant="subtitle1" gutterBottom>
              Manually Enter Barcodes
            </Typography>
          ) : null}
          {manuallyAddedBarcodes.map((item, index) => (
            <Grid
              key={index}
              item
              xs={12}
              container
              spacing={1}
              className={
                item.id === recentlyAddedBarcode ? classes.highlighted : ""
              }
            >
              <Grid item>
                <BarcodeInputIcon isScanned={item.isScanned} />
              </Grid>
              <Grid item xs>
                <Typography>{item.value}</Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="text"
          onClick={() => onSave(totalQuantity)}
          color="default"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
